import { Meta, OperationContext } from '../types';
import { validateMetaStructure } from '../../utils/propertyPanel.ts';
import { collectRelatedFields } from './utils';

// 收集 transform 依赖
const collectTransformDependencies = (
  transforms: Record<string, any>,
  fields: Set<string>
): string[] => {
  return Object.keys(transforms).filter((key) =>
    Array.from(fields).some((field) =>
      JSON.stringify(transforms[key]).includes(field)
    )
  );
};

// 收集 datasource 依赖
const collectDatasourceDependencies = (
  datasource: Record<string, any>,
  fields: Set<string>
): string[] => {
  return Object.keys(datasource).filter((key) =>
    Array.from(fields).some((field) =>
      JSON.stringify(datasource[key]).includes(field)
    )
  );
};

// 清理未使用的 transforms
const removeUnusedTransforms = (
  transforms: Record<string, any> = {},
  unusedTransforms: string[]
): Record<string, any> => {
  if (!transforms) return {};
  return Object.fromEntries(
    Object.entries(transforms).filter(
      ([key]) => !unusedTransforms.includes(key)
    )
  );
};

// 清理未使用的 datasource
const removeUnusedDatasources = (
  datasource: Record<string, any> = {},
  unusedDatasources: string[]
): Record<string, any> => {
  if (!datasource) return {};
  return Object.fromEntries(
    Object.entries(datasource).filter(
      ([key]) => !unusedDatasources.includes(key)
    )
  );
};

// 清理布局配置
const cleanupLayout = (
  layout: Meta['setters']['layout'],
  itemName: string,
  relatedFields: Set<string>
) => {
  return layout.map((section) => ({
    ...section,
    items: section.items
      .map((item) => {
        if (item.name === itemName) return null;
        return {
          ...item,
          fields: cleanupFields(item.fields, itemName, relatedFields),
        };
      })
      .filter(Boolean),
  }));
};

// 清理字段配置
const cleanupFields = (
  fields: any[],
  itemName: string,
  relatedFields: Set<string>
) => {
  return fields
    .map((field) => {
      if (typeof field === "string") {
        const fieldName = field.split("/").pop()!;
        return relatedFields.has(fieldName) ? null : field;
      }
      if (field.name === itemName) return null;
      return {
        ...field,
        fields: field.fields.filter((f: any) => {
          const name = typeof f === "string" ? f.split("/").pop()! : f.name;
          return !relatedFields.has(name);
        }),
      };
    })
    .filter(Boolean);
};

export const createDeleteHandler = (context: OperationContext) => {
  const { meta, onChange, form } = context;

  return async (itemName: string) => {
    try {
      // 1. 收集要删除的字段
      const relatedFields = collectRelatedFields(meta, itemName);

      // 2. 收集相关的 transforms 和 datasource
      const relatedTransforms = collectTransformDependencies(
        meta.transforms || {},
        relatedFields
      );
      const relatedDatasources = collectDatasourceDependencies(
        meta.datasource || {},
        relatedFields
      );

      // 3. 创建新meta
      const newMeta = {
        ...meta,
        setters: {
          props: meta.setters.props.filter(
            (prop) => !relatedFields.has(prop.name)
          ),
          layout: cleanupLayout(meta.setters.layout, itemName, relatedFields),
        },
        defaultValues: {
          props: Object.fromEntries(
            Object.entries(meta.defaultValues.props).filter(
              ([key]) => !relatedFields.has(key)
            )
          ),
        },
        transforms: removeUnusedTransforms(meta.transforms, relatedTransforms),
        datasource: removeUnusedDatasources(
          meta.datasource,
          relatedDatasources
        ),
      };

      // 4. 验证数据结构
      if (!validateMetaStructure(newMeta)) {
        throw new Error("Invalid meta structure after deletion");
      }

      // 5. 清理表单中的相关字段值
      const fieldsToReset = Array.from(relatedFields);
      form.setFields(fieldsToReset.map(field => ({
        name: field,
        value: undefined
      })));

      // 6. 通知更新
      onChange?.({
        type: "setMeta",
        data: newMeta
      });

    } catch (error) {
      console.error("Delete operation failed:", error);
    }
  };
};